import React, {useEffect, useState} from 'react';
import ServiceTypeList from '../../components/General/ServiceTypeList';
import {useForm} from 'react-hook-form';
import {Box, Table, TableContainer, Thead, Tbody, Tr, Td, Th, Button, Flex, Divider, Text, Spinner, IconButton, Icon} from '@chakra-ui/react';
import {useTitle} from '../../components/General/Layout';
import {post, queryStringBuilder} from '../../service/ApiService';
import {Report} from '../../service/Urls';
import ErrorHelper from '../../service/ErrorHelper';
import {get} from '../../service/ApiService';
import {getServiceType} from '../../helpers/ServiceTypeDictionary';
import {DatePicker, CustomCheckbox} from '../../components/General/FormInputs';
import BoxCard from '../../components/General/BoxCard';
import {getFile} from '../../service/ApiService';
import {IoRefreshOutline} from 'react-icons/io5';
import ModalYesNo from '../../components/General/ModalYesNo';
import {FaCheck} from 'react-icons/fa';

const getStatus = (status, exceptionCount) => {
  let name;
  switch (status) {
    case 0:
      name = 'Queued';
      break;
    case 1:
      name = 'Processing';
      break;
    case 2:
      name = 'Complete';
      break;
    case 3:
      name = exceptionCount > 0 ? 'Complete With Errors' : 'Complete with Error';
      break;
    case 4:
      name = exceptionCount > 0 ? 'Errors' : 'Error';
      break;
  }
  return name;
};

const formatDate = dateString => {
  const date = new Date(dateString);
  let formatted = '';
  if (date == null || date == undefined || date == 'Invalid Date') formatted = 'N/A';
  else
    formatted =
      (date.getMonth() > 8 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1)) +
      '/' +
      (date.getDate() > 9 ? date.getDate() : '0' + date.getDate()) +
      '/' +
      date.getFullYear();

  return formatted;
};

export default function SalesClientReports() {
  const [list, setList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [generatingZip, setGeneratingZip] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [toastResponse, setToastResponse] = useState(undefined);

  const [title, setTitle] = useTitle();

  const {
    getValues,
    register,
    handleSubmit,
    control,
    formState: {errors},
  } = useForm();

  const loadData = async () => {
    setLoading(true);
    const response = await get(Report.MONTH_END_LIST);
    setLoading(false);

    setToastResponse({response});

    if (!response.error) {
      setList(response.data?.reverse());
    }

    setTitle('Client Reports for Sales Reps');
  };

  const resetRow = rowId => {
    const newList = list.map(row => {
      if (row.id === rowId) {
        row.fileProcessingStatus = 0;
      }
      return row;
    });

    setList(newList);
  };

  const checkFileExists = () => {
    const data = getValues();

    const fileExists = list.some(row => {
      const start = row.startDate.split('T')[0];
      const end = row.endDate.split('T')[0];

      return row.serviceType === parseInt(data.ServiceType) && start === data.startDate && end === data.endDate && row.isAnnual === data.isAnnual;
    });

    if (fileExists) {
      setModalOpen(true);
    } else {
      handleSubmit(onSubmit(data))();
    }
  };

  const onSubmit = async data => {
    const payload = {
      serviceType: parseInt(data.ServiceType),
      startDate: data.startDate,
      endDate: data.endDate,
      isAnnual: data.isAnnual ?? false,
    };

    setGeneratingZip(true);
    const response = await post(Report.MONTH_END, payload);
    setGeneratingZip(false);

    setToastResponse({response});

    if (!response.error) {
      setList(response.data?.reverse());
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <BoxCard mr={2}>
      <ModalYesNo
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        yesFunction={() => {
          setModalOpen(false);
          handleSubmit(onSubmit)();
        }}
        title="File Already Exists"
        body={`It is recommended that you use the refresh button on the corresponding table entry instead of creating a new one. Would you still like to continue?`}
      />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mt={3}>
            <ServiceTypeList control={control} />
          </Box>
          <Flex mt={3} alignItems="flex-end">
            <DatePicker register={register} id="startDate" name="Start Date" errors={errors} required />
            <DatePicker register={register} id="endDate" name="End Date" errors={errors} required />
            <CustomCheckbox control={control} id="isAnnual" name="Is Annual?" w="125px" p={2} />
            <Button colorScheme="teal" ml={3} disabled={loading} width={160} onClick={checkFileExists}>
              {generatingZip ? <Spinner /> : 'Generate Zip File'}
            </Button>
            <Button colorScheme="blackAlpha" ml={3} disabled={loading} onClick={loadData} width={90}>
              {loading ? <Spinner /> : 'Refresh'}
            </Button>
          </Flex>
        </form>
      </Box>
      <Divider mt={3} colorScheme="mint" />
      <TableContainer>
        <Table variant="striped" colorScheme="mint" size={'md'}>
          <Thead>
            <Tr>
              <Th textAlign="center">Action</Th>
              <Th>Type</Th>
              <Th>Start Date</Th>
              <Th>End Date</Th>
              <Th>Date Created</Th>
              <Th>Is Annual</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {list.map((row, idx) => {
              return (
                <Tr key={row.id}>
                  <Td textAlign="center">
                    <ActionColumn row={row} resetRow={resetRow} setToastResponse={setToastResponse} />
                  </Td>
                  <Td>{getServiceType(row.serviceType)?.key}</Td>
                  <Td>{formatDate(row.startDate)}</Td>
                  <Td>{formatDate(row.endDate)}</Td>
                  <Td>{formatDate(row.dateAdded)}</Td>
                  <Td>{!!row.isAnnual && <Icon as={FaCheck} />}</Td>
                  <Td>
                    <StatusColumn status={row.fileProcessingStatus} errorMessage={row.errorDescription} />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
      <ErrorHelper toastResponse={toastResponse} />
    </BoxCard>
  );
}

const RetryButton = ({row, resetRow, setToastResponse}) => {
  const [loading, setLoading] = useState(false);

  const retry = async () => {
    setLoading(true);
    const response = await post(queryStringBuilder(Report.RESET_MONTH_END, {monthEndId: row.id}));
    setLoading(false);

    setToastResponse({response});

    if (!response.error) {
      resetRow(row.id);
    }
  };

  return (
    <IconButton
      colorScheme="blackAlpha"
      icon={loading ? <Spinner /> : <IoRefreshOutline />}
      aria-label="Retry"
      onClick={retry}
      width={45}
      disabled={loading}
    />
  );
};

const DownloadButton = ({row, setToastResponse}) => {
  const [loading, setLoading] = useState(false);

  const [fileUrl, setFileUrl] = useState(null);

  const linkRef = React.useRef(null);

  const downloadFile = async () => {
    setLoading(true);
    const response = await getFile(queryStringBuilder(Report.MONTH_END_FILE, {monthEndId: row.id}));
    setLoading(false);

    setToastResponse({response});

    if (!response.error) {
      const url = window.URL.createObjectURL(response);

      setFileUrl(url);
    }
  };

  const serviceType = getServiceType(row.serviceType)?.key;

  useEffect(() => {
    if (fileUrl) {
      linkRef.current?.click();
    }
  }, [fileUrl]);

  return (
    <>
      <a
        href={fileUrl}
        rel="noopener noreferrer"
        download={`${serviceType}  Month End ${formatDate(row.startDate)} to ${formatDate(row.endDate)}`}
        ref={linkRef}
        style={{display: 'none'}}
      />
      <Button colorScheme="teal" onClick={downloadFile} disabled={loading} width={110}>
        {loading ? <Spinner /> : 'Download'}
      </Button>
    </>
  );
};

const ActionColumn = ({row, resetRow, setToastResponse}) => {
  if (row.fileProcessingStatus > 2) {
    return <RetryButton row={row} resetRow={resetRow} setToastResponse={setToastResponse} />;
  }

  if (row.fileProcessingStatus === 2) {
    return (
      <Flex gap={2} justifyContent="center">
        <DownloadButton row={row} setToastResponse={setToastResponse} />
        <RetryButton row={row} resetRow={resetRow} setToastResponse={setToastResponse} />
      </Flex>
    );
  }

  return <></>;
};

const StatusColumn = ({status, errorMessage}) => {
  if (status > 2) {
    return (
      <Text color="red.500" flex={1}>
        {`${getStatus(status)} - ${errorMessage ?? 'Unknown'}`}
      </Text>
    );
  }

  return <>{getStatus(status)}</>;
};
